<script setup lang="ts">
import {Dialog, DialogHeader, DialogScrollContent, DialogTitle} from "@/core/components/ui/dialog";
import BaseInput from "@/core/components/base/BaseInput.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {ref} from "vue";
import {UpdateAddressRequest} from "@/core/http/resources/OrganizationResource";
import {organizationApi} from "@/container";
import {Healthcenter} from "@/core/interfaces/healthcenter/Healthcenter";
import {useToastStore} from "@/stores/toast.store";
import {OrganizationAddress} from "@/core/interfaces/organization/Organization";

const {healthcenter} = defineProps<{healthcenter: Healthcenter}>();
const updatedAddress = ref<OrganizationAddress>(healthcenter.address);
const emits = defineEmits(['updateAddress']);
const {showSuccess, showError} = useToastStore();
const dialogOpen = ref<boolean>(false);
const loading = ref<boolean>(false);

/**
 * Update address object.
 */
const updateAddress = () => {
    const request: UpdateAddressRequest = {
        streetLine1: updatedAddress.value.street ?? "",
        streetLine2: updatedAddress.value.street_2 ?? "",
        postalCode: updatedAddress.value.postal_code ?? "",
        cityName: updatedAddress.value.city ?? ""
    };

    loading.value = true;

    organizationApi().updateAddress(updatedAddress.value.id, request)
        .then((response) => {
            emits('updateAddress', response);
            showSuccess({content: 'L\'adresse de votre pharmacie a bien été mise à jour'});
        })
        .catch(() => showError({content: 'Une erreur s\'est produite'}))
        .finally(() => {
            loading.value = false;
            dialogOpen.value = false;
        });
};
</script>

<template>
  <Dialog :open="dialogOpen" @update:open="(value) => dialogOpen = value">
    <DialogTrigger>
      <div class="relative cursor-pointer z-20" @click="dialogOpen = true">
        <div class="absolute w-full h-full z-20" />
        <BaseInput
          label="Adresse"
          :value="healthcenter.address.formatted"
          disabled
          type="text"
          class="rounded-lg"
        />
      </div>
    </DialogTrigger>
    <DialogScrollContent>
      <DialogHeader>
        <DialogTitle>
          Mettre à jour votre adresse
        </DialogTitle>
      </DialogHeader>
      <BaseInput
        label="Adresse"
        :value="updatedAddress.street ?? ''"
        type="text"
        class="rounded-lg"
        @input="value => updatedAddress.street = value"
      />
      <BaseInput
        label="Complément d'adresse"
        :value="healthcenter.address.street_2 ?? ''"
        type="text"
        class="rounded-lg"
        @input="value => updatedAddress.street_2 = value"
      />
      <BaseInput
        label="Ville"
        :value="healthcenter.address.city ?? ''"
        type="text"
        class="rounded-lg"
        @input="value => updatedAddress.city = value"
      />
      <BaseInput
        label="Code postal"
        :value="updatedAddress.postal_code ?? ''"
        type="text"
        class="rounded-lg"
        @input="value => updatedAddress.postal_code = value"
      />
      <div class="flex justify-end">
        <BaseButton primary :loading="loading" @click="updateAddress">
          Enregistrer
        </BaseButton>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>
